import { defineStore } from 'pinia';
import { apiClient } from '@/lib/api/client';
import {
  CouponsWithImageUrl,
  CouponsPaginatedResponse,
  UpdateCouponDto,
} from '@/lib/api/coupons.api';
import {
  CouponApprovals,
  CouponApprovalStatuses,
  CouponRejectionReasons,
} from '@/lib/api/coupon-approvals.api';

export const useCouponStore = defineStore('coupon', {
  state: () => ({
    enableClear: false,
    error: null as Error | null,
    loading: false,
    openImport: false,
    page: 1,
    response: null as CouponsPaginatedResponse | null,
    searchQuery: '',
    selectedCoupon: null as CouponsWithImageUrl | null,
    selectedCoupons: [] as CouponsWithImageUrl[],
    showReport: false,
    visible: false,
  }),

  getters: {
    data: (state): CouponsWithImageUrl[] => state.response?.coupons ?? [],
    hasNextPage: (state): boolean => state.response?.hasNextPage ?? false,
    hasPreviousPage: (state): boolean => state.response?.hasPreviousPage ?? false,
    pageSize: (state): number => state.response?.pageSize ?? 25,
    total: (state): number => state.response?.total ?? 0,
    totalPages(): number {
      return Math.ceil(this.total / this.pageSize);
    },
  },

  actions: {
    async deleteCoupon(couponId: number): Promise<void> {
      const { response } = await apiClient.DELETE('/coupons/{id}', {
        params: {
          path: {
            id: couponId,
          },
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete coupon');
      }
    },

    async deleteCoupons(couponIds: number[]): Promise<void> {
      try {
        this.loading = true;
        await Promise.all(couponIds.map((id) => this.deleteCoupon(id)));
        const idSet = new Set(couponIds);
        this.selectedCoupons = this.selectedCoupons.filter((c) => !idSet.has(c.id));
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async searchCoupons(): Promise<void> {
      try {
        this.error = null;
        this.loading = true;

        const query: {
          page: number;
          pageSize: number;
          sortOrder: 'desc';
          searchQuery?: string;
        } = {
          page: this.page,
          pageSize: this.pageSize,
          sortOrder: 'desc',
        };
        if (this.searchQuery) {
          query.searchQuery = this.searchQuery;
        }
        const { data, response } = await apiClient.GET('/coupons', {
          params: { query },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch coupons');
        }

        this.response = data;
      } catch (error) {
        console.error(error);
        this.error = error;
      } finally {
        this.loading = false;
      }
    },

    resetSelectedCoupon(): void {
      this.selectedCoupon = null;
      this.visible = false;
    },

    setSelectedCoupon(coupon: CouponsWithImageUrl): void {
      this.selectedCoupon = coupon;
      this.visible = true;
    },

    async createCouponApproval(): Promise<CouponApprovals> {
      try {
        this.error = null;
        this.loading = true;

        const { data, response } = await apiClient.POST('/coupon-approvals', {
          body: {
            status: 'approved',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to approve coupon');
        }

        return data;
      } catch (error) {
        this.error = error;
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async rejectCoupon(
      reason: CouponRejectionReasons,
      customReason: string,
    ): Promise<CouponApprovals> {
      try {
        this.error = null;
        this.loading = true;

        const { data, response } = await apiClient.POST('/coupon-approvals', {
          body: {
            customReason: customReason || null,
            reason,
            status: 'rejected',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to reject coupon');
        }

        return data;
      } catch (error) {
        this.error = error;
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async saveApproval(
      couponId: number,
      approvalId: number,
      state: CouponApprovalStatuses,
    ): Promise<void> {
      try {
        this.error = null;
        this.loading = true;

        await apiClient.PATCH('/coupons/{id}', {
          params: {
            path: {
              id: couponId,
            },
          },
          body: {
            approvalId,
            state,
          },
        });
      } catch (error) {
        this.error = error;
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async saveField<T extends keyof UpdateCouponDto>(
      couponId: number,
      param: T,
      value: UpdateCouponDto[T],
    ) {
      try {
        this.isError = false;
        this.loading = true;

        const { response } = await apiClient.PATCH(`/coupons/{id}`, {
          params: {
            path: {
              id: couponId,
            },
          },
          body: {
            [param]: value,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to update coupon');
        }
      } catch (error) {
        this.isError = true;
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.openImport = false;
    },

    async importCouponInOdoo(couponId: number): Promise<void> {
      try {
        this.error = null;
        this.loading = true;

        const { response } = await apiClient.PUT('/coupons/{id}/import-in-odoo', {
          params: {
            path: {
              id: couponId,
            },
          },
        });

        if (!response.ok) {
          throw new Error('Failed to import coupon in Odoo');
        }
      } catch (error) {
        this.error = error as Error;
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
});
